import { supabase } from '../supabase/client';
import { config } from '../utils/config';

export interface AuthAttempt {
  email: string;
  method: 'email' | 'google' | 'github' | 'apple';
  isSignUp: boolean;
}

export interface ExistingAccount {
  id: string;
  email: string;
  providers: string[];
  created_at: string;
  last_sign_in_at: string;
}

/**
 * Normalize Gmail addresses to handle dot aliasing
 * Gmail treats name.familyname@gmail.com and namefamilyname@gmail.com as the same
 */
export function normalizeGmailAddress(email: string): string {
  const lowercaseEmail = email.toLowerCase();
  const [localPart, domain] = lowercaseEmail.split('@');
  
  // Only normalize Gmail addresses
  if (domain === 'gmail.com' || domain === 'googlemail.com') {
    // Remove dots from local part for Gmail
    const normalizedLocal = localPart.replace(/\./g, '');
    return `${normalizedLocal}@gmail.com`; // Always use gmail.com
  }
  
  return lowercaseEmail;
}

/**
 * Check if two email addresses are equivalent (considering Gmail aliasing)
 */
export function areEmailsEquivalent(email1: string, email2: string): boolean {
  return normalizeGmailAddress(email1) === normalizeGmailAddress(email2);
}

/**
 * Check if an account already exists with the given email
 */
export async function checkExistingAccount(email: string): Promise<ExistingAccount | null> {
  try {
    const response = await fetch(`${config.API_URL}/api/auth/check-existing`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json'
      },
      body: JSON.stringify({ email: email.toLowerCase() })
    });

    const data = await response.json();

    if (!response.ok) {
      console.error('Error checking existing account:', data.error);
      return null;
    }

    if (!data.exists) {
      return null;
    }

    return {
      id: data.user_id,
      email: email.toLowerCase(),
      providers: data.providers,
      created_at: data.created_at,
      last_sign_in_at: data.last_sign_in_at
    };

  } catch (error) {
    console.error('Error checking existing account:', error);
    return null;
  }
}

/**
 * Handle authentication attempt with duplicate account prevention
 */
export async function handleAuthAttempt(attempt: AuthAttempt): Promise<{
  success: boolean;
  message: string;
  action: 'allow' | 'redirect_signin' | 'link_account' | 'error';
  existingAccount?: ExistingAccount;
}> {
  
  const existingAccount = await checkExistingAccount(attempt.email);

  // No existing account - allow signup
  if (!existingAccount) {
    return {
      success: true,
      message: 'Proceed with signup',
      action: 'allow'
    };
  }

  // Account exists - handle based on method and intent
  if (attempt.isSignUp) {
    // User trying to sign up but account exists
    const providerNames = existingAccount.providers.map(p => 
      p === 'email' ? 'email/password' : p
    ).join(' or ');

    return {
      success: false,
      message: `An account with this email already exists. Please sign in using ${providerNames}.`,
      action: 'redirect_signin',
      existingAccount
    };
  } else {
    // User trying to sign in
    const hasProvider = existingAccount.providers.includes(attempt.method);
    
    if (hasProvider) {
      // Correct provider - allow signin
      return {
        success: true,
        message: 'Proceed with signin',
        action: 'allow',
        existingAccount
      };
    } else {
      // Wrong provider - suggest correct one
      const correctProvider = existingAccount.providers[0];
      const providerName = correctProvider === 'email' ? 'email/password' : correctProvider;
      
      return {
        success: false,
        message: `This email is registered with ${providerName}. Please sign in using that method.`,
        action: 'redirect_signin',
        existingAccount
      };
    }
  }
}

/**
 * Get user-friendly provider name
 */
export function getProviderDisplayName(provider: string): string {
  switch (provider) {
    case 'email': return 'Email/Password';
    case 'google': return 'Google';
    case 'github': return 'GitHub';
    case 'apple': return 'Apple';
    default: return provider;
  }
}

/**
 * Enhanced sign up with duplicate prevention and forced email confirmation
 */
export async function enhancedSignUp(email: string, password: string) {
  const attempt: AuthAttempt = {
    email: email.toLowerCase(),
    method: 'email',
    isSignUp: true
  };

  const validation = await handleAuthAttempt(attempt);

  if (!validation.success) {
    return {
      data: null,
      error: { message: validation.message },
      action: validation.action,
      existingAccount: validation.existingAccount
    };
  }

  // Proceed with normal signup - force fresh confirmation
  const { data, error } = await supabase.auth.signUp({
    email: email.toLowerCase(),
    password,
    options: {
      data: {
        email_verified: false
      },
      emailRedirectTo: process.env.NODE_ENV === 'development' 
        ? 'http://localhost:3000/login' 
        : `${window.location.origin}/login`
    }
  });

  // If signup succeeded but user might already exist (deleted account case)
  // Force resend confirmation email 
  if (data?.user && !error) {
    try {
      // Small delay then resend confirmation to ensure fresh token
      setTimeout(async () => {
        const { error: resendError } = await supabase.auth.resend({
          type: 'signup',
          email: email.toLowerCase(),
          options: {
            emailRedirectTo: process.env.NODE_ENV === 'development' 
              ? 'http://localhost:3000/login' 
              : `${window.location.origin}/login`
          }
        });
        
        if (resendError) {
          console.log('⚠️ Could not resend confirmation email:', resendError.message);
        } else {
          console.log('✅ Fresh confirmation email sent for re-signup');
        }
      }, 2000);
    } catch (resendError) {
      console.log('⚠️ Resend confirmation failed:', resendError);
    }
  }

  return {
    data,
    error,
    action: 'allow'
  };
}

/**
 * Enhanced sign in with provider validation
 */
export async function enhancedSignIn(email: string, password: string) {
  const attempt: AuthAttempt = {
    email: email.toLowerCase(),
    method: 'email',
    isSignUp: false
  };

  const validation = await handleAuthAttempt(attempt);

  if (!validation.success) {
    return {
      data: null,
      error: { message: validation.message },
      action: validation.action,
      existingAccount: validation.existingAccount
    };
  }

  // Proceed with normal signin
  const { data, error } = await supabase.auth.signInWithPassword({
    email: email.toLowerCase(),
    password
  });

  return {
    data,
    error,
    action: 'allow'
  };
}

/**
 * Enhanced OAuth sign in with duplicate prevention
 */
export async function enhancedOAuthSignIn(provider: 'google' | 'github' | 'apple') {
  // Note: For OAuth, we can't pre-check the email since we don't have it yet
  // The validation will happen in the auth callback
  
  const { data, error } = await supabase.auth.signInWithOAuth({
    provider,
    options: {
      redirectTo: process.env.NODE_ENV === 'production'
        ? `${window.location.origin}/auth/callback`
        : 'http://localhost:3000/auth/callback'
    }
  });

  return { data, error };
}

/**
 * Validate OAuth callback to prevent duplicate accounts
 */
export async function validateOAuthCallback(session: any) {
  if (!session?.user?.email || !session?.access_token) {
    return { success: true, message: 'No validation needed' };
  }

  const email = session.user.email.toLowerCase();
  const provider = session.user.app_metadata?.provider || 'oauth';

  try {
    const response = await fetch(`${config.API_URL}/api/auth/validate-oauth`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json'
      },
      body: JSON.stringify({
        access_token: session.access_token,
        user_email: email,
        provider: provider
      })
    });

    const data = await response.json();

    if (response.status === 409) {
      // Duplicate account detected
      return {
        success: false,
        message: data.message,
        existingAccount: {
          id: '',
          email: email,
          providers: data.existing_providers,
          created_at: '',
          last_sign_in_at: ''
        }
      };
    }

    if (!response.ok) {
      console.error('OAuth validation failed:', data.error);
      return {
        success: false,
        message: 'OAuth validation failed. Please try again.'
      };
    }

    return { 
      success: true, 
      message: 'OAuth validation passed',
      user_id: data.user_id
    };

  } catch (error) {
    console.error('Error validating OAuth:', error);
    return {
      success: false,
      message: 'OAuth validation failed. Please try again.'
    };
  }
} 